import React, { useEffect, useState } from "react";
import { Tag, Tooltip } from "antd";
import { BankOutlined, ClockCircleOutlined, ShopOutlined } from "@ant-design/icons";
import { EVENTO_CAJA_ACTIVA, getInfoCajaActiva } from "../../services/cajaEstadoService";
import { getAllUsers } from "../../services/userService";
import { getAllCajas } from "../../services/cajaService";
import { getAllTPVs } from "../../services/tpvService";
import { useIsMobile } from "../../hooks/useIsMobile";

const CajaStatusIndicator = () => {
  const [cajaInfo, setCajaInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cajaData, setCajaData] = useState({});
  const [userData, setUserData] = useState({});
  const [tpvData, setTpvData] = useState({});
  const isMobile = useIsMobile();

  // Cargar información de la caja activa
  const loadCajaInfo = async () => {
    try {
      const info = await getInfoCajaActiva();
      setCajaInfo(info);
    } catch (error) {
      console.error("Error al cargar información de caja activa:", error);
    } finally {
      setLoading(false);
    }
  };

  // Cargar datos complementarios (nombre de caja y cajero)
  const loadComplementaryData = async () => {
    try {
      const [usuarios, cajas, tpvs] = await Promise.all([
        getAllUsers(),
        getAllCajas(),
        getAllTPVs(),
      ]);

      // Crear mapa de usuarios para acceso rápido por No
      const userMap = {};
      usuarios.forEach((user) => {
        userMap[user.No] = `${user.firstName} ${user.lastName}`;
      });
      setUserData(userMap);

      // Crear mapa de cajas para acceso rápido por No
      const cajaMap = {};
      cajas.forEach((caja) => {
        cajaMap[caja.No] = caja.nombre;
      });
      setCajaData(cajaMap);

      // Crear mapa de tiendas para acceso rápido por No
      const tpvMap = {};
      tpvs.forEach((tpv) => {
        tpvMap[tpv.No] = tpv.nombre;
      });
      setTpvData(tpvMap);
    } catch (error) {
      console.error("Error al cargar datos complementarios:", error);
    }
  };

  // Cargar estado inicial, escuchar los cambios y refrescar cada tanto
  useEffect(() => {
    loadComplementaryData();
    loadCajaInfo();

    // El aviso es lo que hace que esto sea inmediato. Con sólo el reloj de 30
    // segundos, editar la apertura y cambiarle la caja dejaba la píldora
    // anunciando la caja anterior hasta medio minuto después: se veía como si
    // el cambio no se hubiera guardado.
    //
    // Se recargan también los nombres, no sólo la apertura: si la caja nueva se
    // sincronizó después de abrir la pantalla, el mapa no la tendría y la
    // píldora mostraría el código pelado en vez del nombre.
    const refrescar = () => {
      loadComplementaryData();
      loadCajaInfo();
    };

    window.addEventListener(EVENTO_CAJA_ACTIVA, refrescar);

    // El reloj se queda igual, de red: cubre lo que cambia sin pasar por acá
    // (otra pestaña, una sincronización que cierra la caja).
    const interval = setInterval(loadCajaInfo, 30000);

    return () => {
      window.removeEventListener(EVENTO_CAJA_ACTIVA, refrescar);
      clearInterval(interval);
    };
  }, []);

  // No mostrar nada si está cargando o no hay caja abierta
  if (loading) {
    return null;
  }

  if (!cajaInfo) {
    return (
      <div style={{ color: isMobile ? "#ff4d4f" : "inherit" }}>
        <Tooltip title="No hay caja abierta actualmente">
          <Tag
            color="error"
            icon={<BankOutlined />}
            style={{
              fontSize: isMobile ? "11px" : "14px",
              padding: isMobile ? "2px 6px" : "0 8px",
              margin: 0,
            }}
          >
            {isMobile ? "" : "Sin caja abierta"}
          </Tag>
        </Tooltip>
      </div>
    );
  }

  // Obtener nombres descriptivos
  const cajaNombre = cajaData[cajaInfo.Caja] || cajaInfo.Caja;
  const cajeroNombre = userData[cajaInfo.Cajero] || cajaInfo.Cajero;
  const tiendaNombre = tpvData[cajaInfo.TPV] || cajaInfo.TPV;

  // Tienda → Caja → Turno son contexto jerárquico (dónde estoy), no alertas
  // independientes -- van juntos en una sola píldora neutra (un solo tooltip
  // con el detalle completo) en vez de 3 tags de colores que compiten entre
  // sí y con el estado de la licencia.
  return (
    <Tooltip
      title={
        <>
          {tiendaNombre && <div>Tienda: {tiendaNombre}</div>}
          <div>Cajero: {cajeroNombre}</div>
          <div>
            Turno {cajaInfo.Turno} — Abierta el{" "}
            {new Date(cajaInfo.FechaDeApertura).toISOString().split("T")[0]} a las{" "}
            {cajaInfo.HoraDeApertura}
          </div>
        </>
      }
    >
      <Tag
        color="processing"
        style={{
          fontSize: isMobile ? "11px" : "13px",
          padding: isMobile ? "2px 8px" : "2px 10px",
          margin: 0,
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
        }}
      >
        {tiendaNombre && (
          <>
            <ShopOutlined />
            <span>{tiendaNombre}</span>
            <span style={{ opacity: 0.4 }}>›</span>
          </>
        )}
        <BankOutlined />
        <span>{cajaNombre}</span>
        <span style={{ opacity: 0.4 }}>›</span>
        <ClockCircleOutlined />
        <span>Turno {cajaInfo.Turno}</span>
      </Tag>
    </Tooltip>
  );
};

export default CajaStatusIndicator;
